import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Truck, MapPin, CreditCard, RefreshCw, Radio, Hand } from 'lucide-react'
import { api } from '../lib/api'
import { formatCurrency, STATUS_COLORS, STATUS_LABELS } from '../lib/utils'

interface DispatchOrder {
  id: string
  order_number: string
  status: string
  fulfillment: string
  delivery_name: string | null
  delivery_address: string | null
  total_fils: number
  payment_method: string
  driver_id: string | null
  driver_name: string | null
  created_at: string
  dispatch_radius_km: number | null
  dispatch_last_notified_at: string | null
  notified_count: number
}

// After this many minutes of an order being unclaimed, the manual-assign fallback
// becomes prominent. Before that, the self-dispatch state is shown and the manual
// option is hidden behind a smaller "Manually assign" button.
const MANUAL_ASSIGN_THRESHOLD_MINUTES = 10

function minutesSince(iso: string | null): number | null {
  if (!iso) return null
  return Math.floor((Date.now() - new Date(iso + 'Z').getTime()) / 60000)
}

interface Driver {
  id: string
  full_name: string
  phone: string
  active: number
  online: number
  active_orders: number
}

function timeAgo(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso + 'Z').getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  return `${Math.floor(mins / 60)}h ${mins % 60}m ago`
}

export default function Dispatch() {
  const [assigningOrderId, setAssigningOrderId] = useState<string | null>(null)
  const qc = useQueryClient()

  const { data: ordersData, isLoading: ordersLoading } = useQuery<{ orders: DispatchOrder[] }>({
    queryKey: ['dispatch-orders'],
    queryFn: () => api.get('/orders/dispatch').then(r => r.data),
    refetchInterval: 30_000,
  })

  const { data: driversData } = useQuery<{ drivers: Driver[] }>({
    queryKey: ['dispatch-drivers'],
    queryFn: () => api.get('/drivers/dispatch').then(r => r.data),
    refetchInterval: 30_000,
  })

  const assignMutation = useMutation({
    mutationFn: ({ orderId, driverId }: { orderId: string; driverId: string | null }) =>
      api.patch(`/orders/${orderId}/assign-driver`, { driver_id: driverId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dispatch-orders'] })
      qc.invalidateQueries({ queryKey: ['dispatch-drivers'] })
      setAssigningOrderId(null)
    },
  })

  const unassigned = ordersData?.orders.filter(o => !o.driver_id) ?? []
  const active     = ordersData?.orders.filter(o => !!o.driver_id) ?? []

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['dispatch-orders'] })
    qc.invalidateQueries({ queryKey: ['dispatch-drivers'] })
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-white">Dispatch</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Orders self-dispatch to nearby ambassadors. Manual assignment is a fallback for stale orders.
          </p>
        </div>
        <button
          onClick={refresh}
          className="flex items-center gap-2 text-gray-400 hover:text-white text-xs border border-[#2e2e2e] px-3 py-1.5 rounded-lg transition-colors"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Driver roster */}
      <div>
        <h2 className="text-xs text-gray-600 uppercase tracking-widest mb-3">Ambassador Status</h2>
        <div className="flex gap-3 flex-wrap">
          {driversData?.drivers.map(d => (
            <div
              key={d.id}
              className={`flex items-center gap-3 bg-[#1a1a1a] border rounded-xl px-4 py-3 ${
                d.online ? 'border-green-500/25' : 'border-[#2e2e2e]'
              }`}
            >
              <div className="relative">
                <div className="w-8 h-8 rounded-full bg-[#2a2a2a] border border-[#3a3a3a] flex items-center justify-center">
                  <Truck size={14} className={d.online ? 'text-green-400' : 'text-gray-600'} />
                </div>
                <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[#1a1a1a] ${
                  d.online ? 'bg-green-400' : 'bg-gray-600'
                }`} />
              </div>
              <div>
                <p className="text-sm font-medium text-white leading-tight">{d.full_name}</p>
                <p className="text-[11px] text-gray-500">
                  {d.online
                    ? d.active_orders > 0 ? `${d.active_orders} active order${d.active_orders > 1 ? 's' : ''}` : 'Available'
                    : 'Offline'}
                </p>
              </div>
            </div>
          ))}
          {!driversData?.drivers.length && (
            <p className="text-sm text-gray-600 py-2">No active ambassadors — add ambassadors in the Ambassadors page.</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Unassigned queue */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs text-gray-600 uppercase tracking-widest">In Dispatch</h2>
            {unassigned.length > 0 && (
              <span className="text-xs bg-orange-500/15 text-orange-400 px-2 py-0.5 rounded-full">
                {unassigned.length} waiting
              </span>
            )}
          </div>
          <div className="space-y-3">
            {ordersLoading && <div className="text-sm text-gray-600 py-4">Loading…</div>}
            {!ordersLoading && unassigned.length === 0 && (
              <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl px-4 py-10 text-center text-gray-600 text-sm">
                All delivery orders are assigned ✓
              </div>
            )}
            {unassigned.map(order => (
              <OrderCard
                key={order.id}
                order={order}
                drivers={driversData?.drivers ?? []}
                onAssign={() => setAssigningOrderId(order.id)}
                isAssigning={assignMutation.isPending && assigningOrderId === order.id}
              />
            ))}
          </div>
        </div>

        {/* Active deliveries */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs text-gray-600 uppercase tracking-widest">Active Deliveries</h2>
            {active.length > 0 && (
              <span className="text-xs bg-blue-500/15 text-blue-400 px-2 py-0.5 rounded-full">
                {active.length} in progress
              </span>
            )}
          </div>
          <div className="space-y-3">
            {!ordersLoading && active.length === 0 && (
              <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl px-4 py-10 text-center text-gray-600 text-sm">
                No active deliveries right now
              </div>
            )}
            {active.map(order => (
              <OrderCard
                key={order.id}
                order={order}
                drivers={driversData?.drivers ?? []}
                onAssign={() => setAssigningOrderId(order.id)}
                isAssigning={assignMutation.isPending && assigningOrderId === order.id}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Assign modal */}
      {assigningOrderId && (
        <AssignModal
          order={ordersData!.orders.find(o => o.id === assigningOrderId)!}
          drivers={driversData?.drivers ?? []}
          onAssign={(driverId) => assignMutation.mutate({ orderId: assigningOrderId, driverId })}
          onClose={() => setAssigningOrderId(null)}
          isPending={assignMutation.isPending}
          error={assignMutation.error ? String(assignMutation.error) : null}
        />
      )}
    </div>
  )
}

function OrderCard({
  order, drivers, onAssign, isAssigning,
}: {
  order: DispatchOrder
  drivers: Driver[]
  onAssign: () => void
  isAssigning: boolean
}) {
  const assignedDriver = order.driver_id
    ? drivers.find(d => d.id === order.driver_id)
    : null
  const isUnassigned = !order.driver_id
  const orderAgeMin = minutesSince(order.created_at) ?? 0
  // Manual-assign fallback only becomes prominent after the threshold. Until then,
  // the self-dispatch tier is doing its job and the admin shouldn't override it.
  const manualAssignProminent = isUnassigned && orderAgeMin >= MANUAL_ASSIGN_THRESHOLD_MINUTES

  return (
    <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl p-4 space-y-3">
      {/* Top row */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className="text-white font-semibold text-sm">#{order.order_number}</span>
          <span className="text-gray-500 text-xs ml-2">{timeAgo(order.created_at)}</span>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[order.status] ?? ''}`}>
          {STATUS_LABELS[order.status] ?? order.status}
        </span>
      </div>

      {/* Address */}
      {order.delivery_address && (
        <div className="flex items-start gap-2">
          <MapPin size={13} className="text-gray-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-gray-400 leading-relaxed">{order.delivery_address}</p>
        </div>
      )}

      {/* Self-dispatch indicator (only on unassigned orders) */}
      {isUnassigned && order.dispatch_radius_km != null && (
        <DispatchIndicator order={order} stale={manualAssignProminent} />
      )}

      {/* Bottom row */}
      <div className="flex items-center justify-between pt-1">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-white">{formatCurrency(order.total_fils)}</span>
          <div className="flex items-center gap-1">
            <CreditCard size={11} className="text-gray-600" />
            <span className="text-[11px] text-gray-500 uppercase">{order.payment_method}</span>
          </div>
        </div>

        {/* Assignment button: prominent crimson for assigned-driver "Reassign", or for stale
            unassigned orders past the manual-assign threshold. Otherwise a subtle "Manually
            assign" link so the admin can override but isn't pushed to. */}
        {order.driver_id ? (
          <button
            onClick={onAssign}
            disabled={isAssigning}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-[#3a3a3a] text-gray-400 hover:text-white hover:border-[#555] transition-colors"
          >
            <Truck size={12} />
            {assignedDriver ? assignedDriver.full_name.split(' ')[0] : 'Reassign'}
          </button>
        ) : manualAssignProminent ? (
          <button
            onClick={onAssign}
            disabled={isAssigning}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#C41E3A] hover:bg-[#a51830] text-white transition-colors"
          >
            <Hand size={12} />
            Manually assign
          </button>
        ) : (
          <button
            onClick={onAssign}
            disabled={isAssigning}
            className="text-[11px] text-gray-500 hover:text-gray-300 transition-colors underline-offset-2 hover:underline"
          >
            Manually assign
          </button>
        )}
      </div>
    </div>
  )
}

function DispatchIndicator({ order, stale }: { order: DispatchOrder; stale: boolean }) {
  const lastMin = minutesSince(order.dispatch_last_notified_at)
  const lastNotified = lastMin == null
    ? 'never'
    : lastMin < 1 ? 'just now'
    : lastMin < 60 ? `${lastMin}m ago`
    : `${Math.floor(lastMin / 60)}h ${lastMin % 60}m ago`

  return (
    <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border ${
      stale
        ? 'bg-orange-500/10 border-orange-500/30'
        : 'bg-blue-500/5 border-blue-500/20'
    }`}>
      <Radio size={12} className={stale ? 'text-orange-400' : 'text-blue-400'} />
      <p className={`text-[11px] flex-1 ${stale ? 'text-orange-300' : 'text-blue-300/80'}`}>
        Self-dispatch <span className="font-semibold">{order.dispatch_radius_km} km</span>
        {' · '}
        {order.notified_count} ambassador{order.notified_count === 1 ? '' : 's'} notified
        {' · last '}
        {lastNotified}
      </p>
      {stale && (
        <span className="text-[10px] text-orange-400 font-medium uppercase tracking-wide">Stale</span>
      )}
    </div>
  )
}

function AssignModal({
  order, drivers, onAssign, onClose, isPending, error,
}: {
  order: DispatchOrder
  drivers: Driver[]
  onAssign: (driverId: string | null) => void
  onClose: () => void
  isPending: boolean
  error: string | null
}) {
  const availableDrivers = drivers.filter(d => d.active && d.online)
  const allActiveDrivers = drivers.filter(d => d.active)

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2e2e2e]">
          <div>
            <h2 className="text-sm font-semibold text-white">Assign Ambassador</h2>
            <p className="text-xs text-gray-500">Order #{order.order_number}</p>
          </div>
          <button onClick={onClose}><X size={16} className="text-gray-400" /></button>
        </div>

        <div className="p-4 space-y-2">
          {/* Unassign option if currently assigned */}
          {order.driver_id && (
            <button
              onClick={() => onAssign(null)}
              disabled={isPending}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-[#3a3a3a] hover:border-[#555] text-left transition-colors group"
            >
              <div className="w-8 h-8 rounded-full bg-[#2a2a2a] flex items-center justify-center">
                <X size={14} className="text-gray-500" />
              </div>
              <div>
                <p className="text-sm text-gray-300 group-hover:text-white">Unassign Ambassador</p>
                <p className="text-xs text-gray-600">Return to unassigned queue</p>
              </div>
            </button>
          )}

          {/* Online drivers first */}
          {availableDrivers.length > 0 && (
            <p className="text-[10px] text-gray-600 uppercase tracking-widest pt-1 px-1">Online & Available</p>
          )}
          {availableDrivers.map(d => (
            <DriverOption
              key={d.id}
              driver={d}
              isCurrentlyAssigned={order.driver_id === d.id}
              isPending={isPending}
              onSelect={() => onAssign(d.id)}
            />
          ))}

          {/* Offline / busy drivers */}
          {allActiveDrivers.filter(d => !d.online).length > 0 && (
            <>
              <p className="text-[10px] text-gray-600 uppercase tracking-widest pt-1 px-1">Offline Ambassadors</p>
              {allActiveDrivers.filter(d => !d.online).map(d => (
                <DriverOption
                  key={d.id}
                  driver={d}
                  isCurrentlyAssigned={order.driver_id === d.id}
                  isPending={isPending}
                  onSelect={() => onAssign(d.id)}
                />
              ))}
            </>
          )}

          {allActiveDrivers.length === 0 && (
            <p className="text-sm text-gray-600 text-center py-4">No active ambassadors. Add ambassadors first.</p>
          )}
        </div>

        {error && <p className="px-5 pb-4 text-xs text-red-400">{error}</p>}
      </div>
    </div>
  )
}

function DriverOption({
  driver, isCurrentlyAssigned, isPending, onSelect,
}: {
  driver: Driver
  isCurrentlyAssigned: boolean
  isPending: boolean
  onSelect: () => void
}) {
  return (
    <button
      onClick={onSelect}
      disabled={isPending || isCurrentlyAssigned}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors ${
        isCurrentlyAssigned
          ? 'border-[#C41E3A]/40 bg-[#C41E3A]/5 cursor-default'
          : 'border-[#2e2e2e] hover:border-[#555] hover:bg-white/3'
      }`}
    >
      <div className="relative">
        <div className="w-8 h-8 rounded-full bg-[#2a2a2a] border border-[#3a3a3a] flex items-center justify-center">
          <Truck size={14} className={driver.online ? 'text-green-400' : 'text-gray-600'} />
        </div>
        <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[#1a1a1a] ${
          driver.online ? 'bg-green-400' : 'bg-gray-600'
        }`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white truncate">{driver.full_name}</p>
        <p className="text-[11px] text-gray-500">
          {driver.active_orders > 0 ? `${driver.active_orders} active order${driver.active_orders > 1 ? 's' : ''}` : 'No active orders'}
        </p>
      </div>
      {isCurrentlyAssigned && (
        <span className="text-[10px] text-[#C41E3A] font-medium">Assigned</span>
      )}
    </button>
  )
}
